import { useCallback, useEffect, useState } from "react";
import { DropzoneOptions, useDropzone } from "react-dropzone";
import { asyncVoid } from "utils/asyncVoidHandler";
import useLatestRef from "utils/useLatestRef";
import useDropzoneRejectionToast from "components/Shared/useDropzoneRejectionToast";
import type DocumentFileUpload from "./DocumentFileUpload";

export type UseMultipleDocumentUploadOptions<
  R,
  D extends DocumentFileUpload<R>,
> = {
  dropzoneOptions?: Omit<DropzoneOptions, "onDropAccepted" | "onDropRejected">;
  getDocumentFileUploadForFile: (file: File) => D;
  isDroppedRemovedOnComplete?: boolean;
  onUploaded?: (data: R) => void;
};

export default function useMultipleDocumentUpload<
  R,
  D extends DocumentFileUpload<R>,
>({
  dropzoneOptions,
  onUploaded,
  getDocumentFileUploadForFile,
  isDroppedRemovedOnComplete = false,
}: UseMultipleDocumentUploadOptions<R, D>) {
  const [droppedFiles, setDroppedFiles] = useState<D[]>([]);
  const [uploadQueue, setUploadQueue] = useState<D[]>([]);
  const showRejectionToast = useDropzoneRejectionToast();

  const performUpload = async (upload: D) => {
    const document = await upload.startDocumentUpload();

    onUploaded?.(document);

    if (isDroppedRemovedOnComplete) {
      setDroppedFiles((existing) =>
        existing.filter((f) => f.fileUpload.id !== upload.fileUpload.id),
      );
    }

    return document;
  };

  const latestPerformUpload = useLatestRef(performUpload);

  useEffect(() => {
    const nextUpload = uploadQueue[0];
    if (
      nextUpload &&
      !nextUpload.fileUpload.isUploading &&
      !nextUpload.fileUpload.isUploadingToPath
    ) {
      nextUpload.fileUpload.addEventListener("done", () => {
        setUploadQueue((existing) => existing.slice(1));
      });

      asyncVoid(latestPerformUpload.current(nextUpload));
    }
  }, [uploadQueue, latestPerformUpload]);

  const latestGetDocumentFileUploadRef = useLatestRef(
    getDocumentFileUploadForFile,
  );

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles = acceptedFiles.map((file) =>
        latestGetDocumentFileUploadRef.current(file),
      );
      setDroppedFiles((existingFiles) => [...existingFiles, ...newFiles]);
      setUploadQueue((existingFiles) => [...existingFiles, ...newFiles]);
    },
    [setDroppedFiles, setUploadQueue, latestGetDocumentFileUploadRef],
  );

  const dropzone = useDropzone({
    onDropAccepted,
    onDropRejected: showRejectionToast,
    maxSize: 50 * 1e6,
    maxFiles: 5,
    ...dropzoneOptions,
  });

  return { dropzone, droppedFiles };
}
